/**
 * Reconcile Warning Banner — Feb 2026.
 *
 * Detects drift between live entries and the persisted Skating Order
 * Draw and surfaces an amber warning + help popover. Shown on both:
 *   • Competition Draws tile (Documents → Order of Events & Programme)
 *   • Final Programme tile
 *
 * Hides itself when the draw is in sync, no draw exists yet, or the
 * comp isn't set.
 *
 * Props:
 *   competitionId — the currently-selected competition id.
 *   refreshKey    — optional; bump to force a re-check (e.g. after
 *                   applying a reconcile).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export default function ReconcileWarningBanner({ competitionId, refreshKey = 0 }) {
  const [diff, setDiff] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!competitionId) { setDiff(null); return; }
      try {
        const res = await axios.get(
          `${API}/order-of-skate/${competitionId}/reconcile-draw-preview`,
          { withCredentials: true }
        );
        if (!cancelled) setDiff(res.data);
      } catch {
        if (!cancelled) setDiff(null);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [competitionId, refreshKey]);

  // Only surface the warning when a draw exists AND it's out of sync.
  if (!diff || !diff.has_draw || diff.in_sync) return null;

  const s = diff.summary || {};
  const parts = [];
  if (s.names_refreshed) parts.push(`${s.names_refreshed} record${s.names_refreshed === 1 ? '' : 's'} to refresh`);
  if (s.appended) parts.push(`${s.appended} new ${s.appended === 1 ? 'entry' : 'entries'}`);
  if (s.withdrawn) parts.push(`${s.withdrawn} withdrawal${s.withdrawn === 1 ? '' : 's'}`);
  if (s.grade_moves) parts.push(`${s.grade_moves} grade move${s.grade_moves === 1 ? '' : 's'}`);

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-3"
      data-testid="reconcile-warning-banner"
    >
      <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
      <div className="flex-1">
        <div className="flex items-center gap-1.5">
          <p className="font-medium">Draw reconciliation required</p>
          <Popover>
            <PopoverTrigger
              className="text-amber-700 hover:text-amber-900 focus:outline-none"
              aria-label="What does draw reconciliation mean?"
              data-testid="reconcile-warning-help-trigger"
            >
              <HelpCircle className="h-4 w-4" />
            </PopoverTrigger>
            <PopoverContent
              className="w-96 text-sm text-stone-800"
              side="bottom"
              align="start"
              data-testid="reconcile-warning-help-popover"
            >
              <div className="space-y-2">
                <p className="font-semibold text-stone-900">
                  Why reconciliation is needed
                </p>
                <p className="text-xs text-stone-700">
                  When the Skating Order Draw is created it takes a
                  snapshot of every entered skater at that moment.
                  Later edits to entries — name or artistic-number
                  fixes, new late entries, withdrawals, or grade moves
                  — do <span className="font-medium">not</span>{' '}
                  automatically flow into the persisted draw.
                </p>
                <p className="font-semibold text-stone-900 pt-1">
                  How to reconcile
                </p>
                <p className="text-xs text-stone-700">
                  Open the <span className="font-medium">Competition
                  Draws</span> tile and click the{' '}
                  <span className="font-medium">Reconcile Draw</span>{' '}
                  button. A preview shows exactly what will change
                  before anything is written.
                </p>
                <p className="font-semibold text-stone-900 pt-1">
                  What it does
                </p>
                <ul className="text-xs text-stone-700 list-disc pl-4 space-y-0.5">
                  <li>Refreshes stale names / clubs / artistic numbers in place — no position change.</li>
                  <li>Appends new entries to the end of their event with a fresh random number.</li>
                  <li>Removes withdrawn entries.</li>
                  <li>Grade moves: silently removed from the old event and appended to the new one.</li>
                  <li>Existing skaters keep their drawn position.</li>
                </ul>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        {parts.length > 0 && (
          <p className="text-xs mt-1" data-testid="reconcile-warning-summary">
            Pending: {parts.join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}
